import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import type { FieldErrors, Resolver } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { MAILING_TYPES, MAILING_TYPE_LABEL, US_STATES, enrollmentStep2Schema } from '@app/shared';
import type { EnrollmentDto, EnrollmentPlanOption, EnrollmentStep2Input } from '@app/shared';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/PageHeader';
import { ErrorState, LoadingState } from '../../components/PageState';
import { SelectInput, TextInput, applyServerErrors } from '../../components/form';
import { formatCents } from '../../lib/format';
import { useEnrollmentPlans, useSaveStep2 } from './api';
import { stepPath } from './labels';
import { ProblemList, WizardGate, flattenErrors } from './wizard';

const TITLE = 'Enrollment Step 2';

const STATE_OPTIONS = US_STATES.map(([code, name]) => ({
  value: code,
  label: `${name} [${code}]`,
}));
const stateLabel = (code: string) => STATE_OPTIONS.find((s) => s.value === code)?.label ?? code;

/** Fields in the order of the page; the list of problems at the top follows it. */
const FIELD_ORDER = [
  'firstName',
  'middleInitial',
  'lastName',
  'addressLine1',
  'addressLine2',
  'city',
  'mailingType',
  'mailing.line1',
  'mailing.line2',
  'mailing.zip',
  'mailing.city',
  'mailing.state',
  'phone',
  'email',
  'planId',
];

const schemaResolver = zodResolver(enrollmentStep2Schema);

/**
 * The shared schema, plus two things it cannot know: the mailing fields are only part of the
 * form when the mailing address differs, and "Email Mandatory" is a system setting.
 */
const resolverFor =
  (emailMandatory: boolean): Resolver<EnrollmentStep2Input> =>
  async (values, context, options) => {
    const result = await schemaResolver(
      { ...values, mailing: values.mailingType === 'same' ? null : values.mailing },
      context,
      options,
    );
    const errors: FieldErrors<EnrollmentStep2Input> = result.errors;
    // An empty select reads "State is required." instead of the schema's enum message.
    if (errors.mailing?.state && !values.mailing?.state)
      errors.mailing.state.message = 'State is required.';
    if (emailMandatory && !(values.email ?? '').trim() && !errors.email)
      return {
        values: {},
        errors: { ...errors, email: { type: 'required', message: 'Email is required.' } },
      };
    return result;
  };

/** What the form starts with: the saved applicant when step 2 was done before, else the ZIP's city. */
function initialValues(
  enrollment: EnrollmentDto,
  offered: EnrollmentPlanOption[],
): EnrollmentStep2Input {
  const a = enrollment.applicant;
  const savedPlan = enrollment.plan?.id;
  return {
    firstName: a?.firstName ?? '',
    middleInitial: a?.middleInitial ?? '',
    lastName: a?.lastName ?? '',
    addressLine1: a?.addressLine1 ?? '',
    addressLine2: a?.addressLine2 ?? '',
    city: a?.city ?? enrollment.city,
    mailingType: a?.mailingType ?? 'same',
    mailing: {
      line1: a?.mailing?.line1 ?? '',
      line2: a?.mailing?.line2 ?? '',
      zip: a?.mailing?.zip ?? '',
      city: a?.mailing?.city ?? '',
      state: a?.mailing?.state ?? '',
    },
    phone: a?.phone ?? '',
    email: a?.email ?? '',
    // A plan that is no longer offered has to be chosen again.
    planId: savedPlan && offered.some((p) => p.id === savedPlan) ? savedPlan : '',
  };
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box component="fieldset" sx={{ border: 0, p: 0, m: 0, mb: 2.5, minWidth: 0 }}>
      <Typography component="legend" sx={{ fontWeight: 600, mb: 1.25, p: 0 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

const grid = (columns: number) => ({
  display: 'grid',
  gap: 2,
  gridTemplateColumns: { xs: '1fr', sm: `repeat(${columns}, 1fr)` },
  alignItems: 'start',
});

function Step2Form({
  enrollment,
  plans,
  emailMandatory,
}: {
  enrollment: EnrollmentDto;
  plans: EnrollmentPlanOption[];
  emailMandatory: boolean;
}) {
  const navigate = useNavigate();
  const notify = useNotify();
  const save = useSaveStep2(enrollment.id);
  const resolver = useMemo(() => resolverFor(emailMandatory), [emailMandatory]);
  const { control, handleSubmit, setError, formState } = useForm<EnrollmentStep2Input>({
    resolver,
    defaultValues: initialValues(enrollment, plans),
  });
  const mailingType = useWatch({ control, name: 'mailingType' });

  const problems = flattenErrors(formState.errors, FIELD_ORDER);
  const problemList = useRef<HTMLDivElement>(null);
  // After a refused submit the list is at the top of a long form; bring it into view.
  useEffect(() => {
    problemList.current?.scrollIntoView?.({ block: 'nearest' });
  }, [formState.submitCount]);

  const submit = handleSubmit(async (values) => {
    try {
      // The resolver hands over the parsed schema output, which is what the API expects.
      const saved = await save.mutateAsync(values);
      navigate(stepPath(saved.id, 3));
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });

  return (
    <Paper
      variant="outlined"
      component="form"
      noValidate
      onSubmit={(e) => void submit(e)}
      aria-label={TITLE}
      sx={{ p: 2, maxWidth: 1040 }}
    >
      <Typography variant="h3" sx={{ mb: 1 }}>
        Personal Information For Prepaid/Postpaid Customer
      </Typography>
      <Typography>Enrollment ID: {enrollment.enrollmentId}</Typography>
      <Typography sx={{ mb: 2 }}>
        This will be the primary account: {enrollment.enrollmentId}
      </Typography>

      <ProblemList ref={problemList} problems={problems} />

      <Section title="Name">
        <Box sx={grid(3)}>
          <TextInput
            control={control}
            name="firstName"
            label="First Name"
            required
            slotProps={{ htmlInput: { maxLength: 30 } }}
          />
          <TextInput
            control={control}
            name="middleInitial"
            label="Middle Initial"
            slotProps={{ htmlInput: { maxLength: 1 } }}
          />
          <TextInput
            control={control}
            name="lastName"
            label="Last Name"
            required
            slotProps={{ htmlInput: { maxLength: 30 } }}
          />
        </Box>
      </Section>

      <Section title="Applicant Address/Billing Address/Service Address (PO BOX are not allowed)*">
        <Box sx={grid(2)}>
          <TextInput
            control={control}
            name="addressLine1"
            label="Address 1"
            required
            slotProps={{ htmlInput: { maxLength: 120 } }}
          />
          <TextInput
            control={control}
            name="addressLine2"
            label="Address 2"
            slotProps={{ htmlInput: { maxLength: 120 } }}
          />
        </Box>
        <Box sx={{ ...grid(3), mt: 2 }}>
          {/* ZIP and state were fixed by step 1 and cannot be changed here. */}
          <TextField
            label="Zip Code"
            required
            value={enrollment.zip}
            slotProps={{ input: { readOnly: true } }}
          />
          <TextInput
            control={control}
            name="city"
            label="City"
            required
            slotProps={{ htmlInput: { maxLength: 60 } }}
          />
          <TextField
            label="State"
            required
            value={stateLabel(enrollment.state)}
            slotProps={{ input: { readOnly: true } }}
          />
        </Box>
      </Section>

      <Box sx={{ mb: 2.5 }}>
        <Controller
          control={control}
          name="mailingType"
          render={({ field }) => (
            <FormControl>
              <RadioGroup
                row
                aria-label="Mailing address"
                name={field.name}
                value={field.value ?? 'same'}
                onChange={(e) => field.onChange(e.target.value)}
                onBlur={field.onBlur}
              >
                {MAILING_TYPES.map((type) => (
                  <FormControlLabel
                    key={type}
                    value={type}
                    label={MAILING_TYPE_LABEL[type]}
                    control={<Radio size="small" />}
                  />
                ))}
              </RadioGroup>
              <FormHelperText sx={{ ml: 0 }}>
                At the time of payment credit card billing address must be same as shipping address
              </FormHelperText>
            </FormControl>
          )}
        />
        {mailingType && mailingType !== 'same' ? (
          <>
            <Box sx={{ ...grid(2), mt: 2 }}>
              <TextInput
                control={control}
                name="mailing.line1"
                label="Mailing address 1"
                required
                slotProps={{ htmlInput: { maxLength: 120 } }}
              />
              <TextInput
                control={control}
                name="mailing.line2"
                label="Mailing address 2"
                slotProps={{ htmlInput: { maxLength: 120 } }}
              />
            </Box>
            <Box sx={{ ...grid(3), mt: 2 }}>
              <TextInput
                control={control}
                name="mailing.zip"
                label="Zip code"
                required
                slotProps={{ htmlInput: { maxLength: 5, inputMode: 'numeric' } }}
              />
              <TextInput
                control={control}
                name="mailing.city"
                label="City"
                required
                slotProps={{ htmlInput: { maxLength: 60 } }}
              />
              <SelectInput
                control={control}
                name="mailing.state"
                label="State"
                required
                options={STATE_OPTIONS}
              />
            </Box>
          </>
        ) : null}
      </Box>

      <Section title="Contact Details">
        <Box sx={grid(2)}>
          <TextInput
            control={control}
            name="phone"
            label="Phone Number"
            slotProps={{ htmlInput: { maxLength: 10, inputMode: 'numeric' } }}
          />
          <TextInput
            control={control}
            name="email"
            label="Email Address"
            type="email"
            required={emailMandatory}
            slotProps={{ htmlInput: { maxLength: 120 } }}
          />
        </Box>
      </Section>

      <Box sx={{ mb: 2.5 }}>
        {plans.length === 0 ? (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            No plan is live in {stateLabel(enrollment.state)} for your sales channel, so this
            enrollment cannot continue.
          </Alert>
        ) : null}
        <Box sx={grid(2)}>
          <SelectInput
            control={control}
            name="planId"
            label="Choose plan"
            required
            options={plans.map((p) => ({
              value: p.id,
              label: `${p.name} - ${formatCents(p.priceCents)}`,
            }))}
          />
        </Box>
      </Box>

      {/* The reference also has "Add More" for further lines on the same order; multi-line
          orders are not built, so the button is not shown. */}
      <Button type="submit" variant="contained" disabled={save.isPending}>
        Next Step
      </Button>
    </Paper>
  );
}

function Step2({ enrollment }: { enrollment: EnrollmentDto }) {
  const plans = useEnrollmentPlans(enrollment.id);
  return (
    <Box>
      <PageHeader title={TITLE} code="PC1142" />
      {plans.error ? (
        <ErrorState error={plans.error} onRetry={() => void plans.refetch()} />
      ) : !plans.data ? (
        // The form starts from the saved plan, so the plans have to be there first.
        <LoadingState />
      ) : (
        <Step2Form
          enrollment={enrollment}
          plans={plans.data.data}
          emailMandatory={plans.data.emailMandatory}
        />
      )}
    </Box>
  );
}

/** "Enrollment Step 2" (PC1142): personal information, addresses, contact details and the plan. */
export function Step2Page() {
  return (
    <WizardGate step={2} title={TITLE}>
      {(enrollment) => <Step2 key={enrollment.id} enrollment={enrollment} />}
    </WizardGate>
  );
}
